import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

import { createGlassSlice } from './slices/glassSlice';
import { createPreferencesSlice } from './slices/preferencesSlice';
import { createThemeSlice } from './slices/themeSlice';
import { createUiSlice } from './slices/uiSlice';
import type { AppState } from './types';

/**
 * The single application store, composed of slices.
 *
 * `subscribeWithSelector` is load-bearing: the systems (ThemeManager,
 * ScrollManager) are plain classes outside React, and they subscribe to
 * individual fields imperatively:
 *
 *   useAppStore.subscribe((s) => s.isMenuOpen, (open) => ...);
 *
 * Read the contract in ./types.ts before adding a field.
 */
export const useAppStore = create<AppState>()(
  subscribeWithSelector((...args) => ({
    ...createThemeSlice(...args),
    ...createGlassSlice(...args),
    ...createUiSlice(...args),
    ...createPreferencesSlice(...args),
  })),
);

/** Imperative accessor for non-React code. */
export const getAppState = (): AppState => useAppStore.getState();
